/**
 * useScrollableCarousel
 *
 * A plain scrollable div carousel hook, created to replace Swiper in the
 * SideBySideComparison edit view. Swiper's pointer event handling conflicted
 * with @dnd-kit. This hook gives us full control: mouse-drag-to-scroll,
 * snap-to-nearest-item on release, nav button scrolling, and the ability to
 * pause/resume scroll listeners around dnd-kit drag sessions.
 */
import { useCallback, useRef, useState } from "react";

export const useScrollableCarousel = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const isMouseDownRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);

  const updateNavState = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setIsBeginning(el.scrollLeft <= 0);
    setIsEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  const scrollSlider = useCallback((direction: "left" | "right") => {
    const el = containerRef.current;
    if (!el) return;
    const children = Array.from(el.children) as HTMLElement[];
    const containerLeft = el.getBoundingClientRect().left;

    if (direction === "right") {
      const next = children.find(
        (c) => c.getBoundingClientRect().left > containerLeft + 1,
      );
      if (next) {
        el.scrollTo({
          left:
            el.scrollLeft + next.getBoundingClientRect().left - containerLeft,
          behavior: "smooth",
        });
      }
    } else {
      const prev = [...children]
        .reverse()
        .find((c) => c.getBoundingClientRect().left < containerLeft - 1);
      if (prev) {
        el.scrollTo({
          left:
            el.scrollLeft + prev.getBoundingClientRect().left - containerLeft,
          behavior: "smooth",
        });
      }
    }
  }, []);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    const el = containerRef.current;
    if (!el) return;
    isMouseDownRef.current = true;
    startXRef.current = e.pageX - el.offsetLeft;
    scrollLeftRef.current = el.scrollLeft;
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isMouseDownRef.current) return;
      const el = containerRef.current;
      if (!el) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = x - startXRef.current;
      el.scrollLeft = scrollLeftRef.current - walk;
      updateNavState();
    },
    [updateNavState],
  );

  const snapToNearest = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const children = Array.from(el.children) as HTMLElement[];
    const containerLeft = el.getBoundingClientRect().left;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;

    const itemTargets = children.map(
      (c) => el.scrollLeft + c.getBoundingClientRect().left - containerLeft,
    );

    const snapTargets = [...new Set([...itemTargets, maxScrollLeft])];

    const target = snapTargets.reduce((best, curr) =>
      Math.abs(curr - el.scrollLeft) < Math.abs(best - el.scrollLeft)
        ? curr
        : best,
    );

    if (Math.abs(target - el.scrollLeft) > 1) {
      el.scrollTo({ left: target, behavior: "smooth" });
    }
  }, []);

  const handleMouseUpOrLeave = useCallback(() => {
    if (!isMouseDownRef.current) return;
    isMouseDownRef.current = false;
    snapToNearest();
  }, [snapToNearest]);

  const attachScrollListeners = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("mousedown", handleMouseDown);
    el.addEventListener("mousemove", handleMouseMove);
    el.addEventListener("mouseup", handleMouseUpOrLeave);
    el.addEventListener("mouseleave", handleMouseUpOrLeave);
    el.addEventListener("scroll", updateNavState);
    updateNavState();
  }, [handleMouseDown, handleMouseMove, handleMouseUpOrLeave, updateNavState]);

  const detachScrollListeners = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    isMouseDownRef.current = false;
    el.removeEventListener("mousedown", handleMouseDown);
    el.removeEventListener("mousemove", handleMouseMove);
    el.removeEventListener("mouseup", handleMouseUpOrLeave);
    el.removeEventListener("mouseleave", handleMouseUpOrLeave);
    el.removeEventListener("scroll", updateNavState);
  }, [handleMouseDown, handleMouseMove, handleMouseUpOrLeave, updateNavState]);

  return {
    containerRef,
    isBeginning,
    isEnd,
    scrollSliderLeft: () => scrollSlider("left"),
    scrollSliderRight: () => scrollSlider("right"),
    attachScrollListeners,
    detachScrollListeners,
    getContainerEl: useCallback(() => containerRef.current, []),
  };
};
